import { GridSortDirection, GridSortModel } from '@mui/x-data-grid-pro'

export const pageSizeOptions = [50, 75, 100]

export const sortingOrder: GridSortDirection[] = ['desc', 'asc', null]

const KEY_SEPARATOR = ','
const COLUMN_SEPARATOR = ', '

/**
 * Describes how a single OData sort key relates to the grid.
 *
 * `column` is the grid column `field` that represents the key, for lists where
 * the two differ — e.g. Leads renders `createdDate` in a column keyed `id`.
 * Without it the sort model points at a column that does not exist and the grid
 * silently drops it, resetting the sort to the default.
 *
 * `tiebreakers` are extra OData keys appended after the primary key, sorted in
 * the same direction, so the round trip reproduces the exact `orderBy` string
 * used by the filter panel's Sort By options.
 */
export type SortKeyConfig = {
  column?: string
  tiebreakers?: string[]
}

export type SortConfig = Record<string, SortKeyConfig>

const splitOrderBy = (orderBy: string): string[] =>
  orderBy
    .split(KEY_SEPARATOR)
    .map((key) => key.trim())
    .filter(Boolean)

const keyToColumn = (config: SortConfig, key: string): string =>
  config[key]?.column ?? key

const columnToKey = (config: SortConfig, column: string): string =>
  Object.keys(config).find((key) => config[key].column === column) ?? column

export const sortModelToOrderBy = (
  sortModel: GridSortModel,
  config: SortConfig = {},
): string =>
  sortModel
    .reduce<string[]>((prev, { field, sort }) => {
      const key = columnToKey(config, field)
      const tiebreakers = config[key]?.tiebreakers ?? []
      return [
        ...prev,
        [
          `${key} ${sort}`,
          ...tiebreakers.map((tiebreaker) => `${tiebreaker} ${sort}`),
        ].join(KEY_SEPARATOR),
      ]
    }, [])
    .join(COLUMN_SEPARATOR)

export const orderByToSortModel = (
  orderBy: string,
  config: SortConfig = {},
): GridSortModel => {
  const consumed = new Set<string>()
  return splitOrderBy(orderBy).reduce<GridSortModel>((prev, curr) => {
    const [key, sortStr] = curr.split(' ')
    if (consumed.has(key)) return prev
    const column = keyToColumn(config, key)
    if (prev.some((item) => item.field === column)) return prev
    config[key]?.tiebreakers?.forEach((tiebreaker) => consumed.add(tiebreaker))
    return [...prev, { field: column, sort: sortStr as GridSortDirection }]
  }, [])
}

export const getOrderByFunc =
  (defaultOrderBy: string, config: SortConfig = {}) =>
  (sortModel: GridSortModel) =>
    sortModel.length === 0
      ? defaultOrderBy
      : sortModelToOrderBy(sortModel, config)
